<template>
  <section
    class="section product-carousel-section"
    v-if="products && products.length"
  >
    <div class="wrapper">
      <div class="header">
        <h2 class="section-title title text-white">{{ title }}</h2>
      </div>
      <Transition>
        <div v-if="products && products.length" class="product-list-slider">
          <swiper
            ref="swiperRef"
            :modules="modules"
            :slides-per-view="'auto'"
            :space-between="16"
            :free-mode="true"
            :navigation="{
              prevEl: '.swiper-button-prev',
              nextEl: '.swiper-button-next',
            }"
          >
            <swiper-slide v-for="(item, i) in products" :key="i">
              <ProductCard
                :item="item"
                :chosenItem="chosenItem"
                :currencySymbol="currencySymbol"
                @goToProduct="goToProduct"
                @addToCart="addToCart"
              />
            </swiper-slide>
          </swiper>
        </div>
      </Transition>
      <div class="navigation-controls">
        <template v-if="showArrows">
          <button class="pagination-button">
            <div class="swiper-button-prev"></div>
          </button>
          <button class="pagination-button">
            <div class="swiper-button-next"></div>
          </button>
        </template>
      </div>
      <router-link :to="showAllLink" class="button red w-220">
        {{ $t("Browse all skins") }}
      </router-link>
    </div>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import ProductCard from "@/components/ProductCard.vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/free-mode";

export default {
  name: "ProductCarousel",
  components: {
    ProductCard,
    Swiper,
    SwiperSlide,
  },
  props: {
    chosenItem: {
      type: String,
    },
    title: {
      type: String,
      required: true,
    },
    showAllLink: {
      type: String,
      default: "/products/all",
    },
    apiEndpoint: {
      type: String,
      default: "",
    },
    showArrows: {
      type: Boolean,
      default: false,
    },
    initialProducts: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      products: [],
      swiperRef: null,
      modules: [Navigation],
    };
  },
  watch: {
    currencyCode() {
      this.fetchProducts();
    },
    initialProducts: {
      deep: true,
      handler(products) {
        if (products && products.length > 0) {
          this.products = products;
        }
      },
    },
  },
  computed: {
    ...mapGetters("app", ["currencyCode", "currencySymbol"]),
  },
  mounted() {
    if (this.initialProducts && this.initialProducts.length > 0) {
      this.products = this.initialProducts;
    } else {
      this.fetchProducts();
    }
    this.$nextTick(() => {
      if (this.$refs.swiperRef && this.$refs.swiperRef.swiper) {
        this.$refs.swiperRef.swiper.on("init", () => {
          this.swiperRef = this.$refs.swiperRef.swiper;
        });
      }
    });
  },
  methods: {
    addToCart(item) {
      this.$emit("addToCart", item);
    },
    fetchProducts() {
      if (!this.apiEndpoint) {
        return;
      }

      this.$http
        .get(process.env.VUE_APP_API + this.apiEndpoint)
        .then((res) => {
          this.products = res.data.payload;
        })
        .catch((res) => {
          if (res.response.status === 401) {
            this.openSignInModal();
          }
        });
    },
    goToProduct(item) {
      this.$emit("goToProduct", item);
    },
    slidePrev() {
      if (this.swiperRef) {
        this.swiperRef.slidePrev();
      }
    },
    slideNext() {
      if (this.swiperRef) {
        this.swiperRef.slideNext();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.product-carousel-section {
  color: #ffffff;
  padding: 90px 16px 80px;

  .wrapper {
    padding: 0;
    position: relative;
  }
  .button {
    display: table;
    margin: 30px auto 0;
  }
}

.header {
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.view-all-link {
  font-family: "Roboto", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #9d1616;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  gap: 12px;
  align-items: center;
  display: inline-flex;
}

.swiper {
  width: 100%;
}

.swiper-slide {
  width: 208px;
  will-change: transform;
}

.product-list-slider {
  margin-top: 30px;
}
.navigation-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  width: 100%;
  z-index: 3;
  position: relative;
}

.swiper-button-prev,
.swiper-button-next {
  position: relative;
  transform: none;
  margin: 0;
  padding: 0;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  content: none;
  z-index: 3;

  width: 100%;
  height: 100%;
}

.swiper-button-prev::after,
.swiper-button-next::before {
  content: "";
}

.swiper-button-prev::before {
  content: "";
  background-image: url("@/assets/img/arrow-left-new.svg");
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  width: 20px;
  height: 20px;
  display: inline-block;
  margin: 0 auto;
}

.swiper-button-next::after {
  content: "";
  background-image: url("@/assets/img/arrow-right-new.svg");
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  width: 20px;
  height: 20px;
  display: inline-block;
  margin: 0 auto;
}

@media screen and (max-width: 768px) {
  .header {
    flex-direction: column;
    gap: 40px;
    text-align: center;
  }
  .view-all-link {
    position: static;
  }
  .product-list-slider {
    margin-top: 24px;
  }
  .navigation-controls {
    margin-top: 16px;
  }
}
</style>
